<template>
    <section class="drawer-activity" aria-labelledby="drawer-activity-title">
        <div class="activity-heading">
            <div>
                <h2 id="drawer-activity-title">Activity</h2>
                <span class="activity-count">{{ totalResults }} {{ totalResults === 1 ? "record" : "records" }}</span>
            </div>
            <button
                v-if="hasActiveFilters"
                type="button"
                class="clear-all-button"
                @click="clearFilters"
            >
                Clear filters
            </button>
        </div>

        <div ref="toolbar" class="activity-toolbar">
            <label class="search-control">
                <i class="material-icons" aria-hidden="true">search</i>
                <span class="sr-only">Search drawer activity</span>
                <input
                    v-model="searchValue"
                    type="search"
                    placeholder="Search transaction, employee, or type..."
                    @input="queueSearch"
                />
            </label>

            <details ref="filtersMenu" class="filters-menu" @keydown.esc.prevent="closeFilters">
                <summary class="filters-trigger">
                    <i class="material-icons" aria-hidden="true">filter_list</i>
                    <span>Filters</span>
                    <span v-if="activeFilterCount" class="filter-count">{{ activeFilterCount }}</span>
                    <i class="material-icons filter-chevron" aria-hidden="true">expand_more</i>
                </summary>

                <div class="filters-popover">
                    <div class="popover-heading">
                        <strong>Filter activity</strong>
                        <span>Narrow this drawer ledger</span>
                    </div>

                    <label class="filter-field">
                        <span>Transaction type</span>
                        <select v-model="filters.transactionType" @change="applySelection">
                            <option value="">All transaction types</option>
                            <option
                                v-for="type in filterOptions.transactionTypes"
                                :key="`${type.source}:${type.code}`"
                                :value="type.code"
                            >
                                {{ type.name }}
                            </option>
                        </select>
                    </label>

                    <label class="filter-field">
                        <span>Payment method</span>
                        <select v-model="filters.paymentMethodId" @change="applySelection">
                            <option value="">All payment methods</option>
                            <option
                                v-for="paymentMethod in filterOptions.paymentMethods"
                                :key="paymentMethod.id"
                                :value="paymentMethod.id"
                            >
                                {{ paymentMethod.name }}
                            </option>
                        </select>
                    </label>

                    <label class="filter-field">
                        <span>Employee</span>
                        <select v-model="filters.userKey" @change="applySelection">
                            <option value="">All employees</option>
                            <option
                                v-for="employee in filterOptions.users"
                                :key="employeeOptionKey(employee)"
                                :value="employeeOptionKey(employee)"
                            >
                                {{ employee.name }}
                            </option>
                        </select>
                    </label>
                </div>
            </details>

            <div v-if="activeChips.length" class="active-filter-chips" aria-label="Active activity filters">
                <button
                    v-for="chip in activeChips"
                    :key="chip.key"
                    type="button"
                    class="filter-chip"
                    :aria-label="`Remove ${chip.label} filter`"
                    @click="removeFilter(chip.key)"
                >
                    <span>{{ chip.label }}</span>
                    <i class="material-icons" aria-hidden="true">close</i>
                </button>
            </div>
        </div>

        <div v-if="error" class="activity-state error-state" role="alert">
            <span>{{ error }}</span>
            <button type="button" @click="fetchActivity">Try again</button>
        </div>

        <div v-else class="activity-table-wrap">
            <table class="activity-table">
                <thead>
                    <tr>
                        <th scope="col">
                            <button type="button" class="sort-button" @click="changeSort('occurred_at')">
                                Date &amp; time <SortIcon :active="sortBy === 'occurred_at'" :direction="sortOrder" />
                            </button>
                        </th>
                        <th scope="col">
                            <button type="button" class="sort-button" @click="changeSort('type')">
                                Type <SortIcon :active="sortBy === 'type'" :direction="sortOrder" />
                            </button>
                        </th>
                        <th scope="col">Transaction #</th>
                        <th scope="col">Payment method</th>
                        <th scope="col" class="user-column">
                            <button type="button" class="sort-button" @click="changeSort('user')">
                                User <SortIcon :active="sortBy === 'user'" :direction="sortOrder" />
                            </button>
                        </th>
                        <th scope="col" class="amount-column">
                            <button type="button" class="sort-button amount-sort" @click="changeSort('amount')">
                                Amount <SortIcon :active="sortBy === 'amount'" :direction="sortOrder" />
                            </button>
                        </th>
                        <th v-if="showNotes" scope="col" class="notes-column">Notes</th>
                        <th scope="col" class="device-column">Device</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td :colspan="columnCount" class="activity-state">Loading activity...</td>
                    </tr>
                    <tr v-else-if="!rows.length">
                        <td :colspan="columnCount" class="activity-state">
                            {{ hasActiveFilters ? "No activity matches these filters." : "No drawer activity found." }}
                        </td>
                    </tr>
                    <tr v-for="row in rows" v-else :key="row.id">
                        <td class="date-cell">{{ formatDate(row.occurredAt) }}</td>
                        <td>
                            <span :class="['type-badge', typeTone(row.transactionTypeCode)]">
                                {{ row.transactionTypeName }}
                            </span>
                        </td>
                        <td class="reference-cell">
                            <router-link v-if="canOpenDetail(row)" :to="row.detailPath">{{ row.reference }}</router-link>
                            <span v-else>{{ row.reference }}</span>
                        </td>
                        <td>
                            <span v-if="!row.paymentMethods.length" class="empty-value">—</span>
                            <span v-else class="payment-summary" :title="paymentTitle(row.paymentMethods)">
                                {{ row.paymentMethods[0].name }}
                                <span v-if="row.paymentMethods.length > 1" class="payment-more">
                                    +{{ row.paymentMethods.length - 1 }}
                                </span>
                            </span>
                        </td>
                        <td class="user-column">{{ row.actorName || "Unknown" }}</td>
                        <td :class="['amount-column', { refund: isRefund(row) }]">{{ formatCurrency(row.amount, row) }}</td>
                        <td v-if="showNotes" class="notes-column">
                            <span v-if="!noteText(row)" class="empty-value">—</span>
                            <button
                                v-else
                                type="button"
                                :class="['notes-cell', { expanded: expandedNoteId === row.id }]"
                                :title="noteText(row)"
                                :aria-expanded="String(expandedNoteId === row.id)"
                                @click="toggleNote(row.id)"
                            >
                                {{ noteText(row) }}
                            </button>
                        </td>
                        <td class="device-column">{{ row.deviceId || "—" }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <footer v-if="!error && totalResults" class="activity-footer">
            <span>Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalResults }}</span>
            <div v-if="totalPages > 1" class="pagination-controls">
                <button type="button" :disabled="page <= 1 || loading" @click="changePage(page - 1)">
                    <i class="material-icons" aria-hidden="true">chevron_left</i>
                    <span>Previous</span>
                </button>
                <span>Page {{ page }} of {{ totalPages }}</span>
                <button type="button" :disabled="page >= totalPages || loading" @click="changePage(page + 1)">
                    <span>Next</span>
                    <i class="material-icons" aria-hidden="true">chevron_right</i>
                </button>
            </div>
        </footer>
    </section>
</template>

<script>
import { mapState } from "vuex"
import CashManagementFetchApi from "../../utils/fetch/cash-management-fetch-api"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"

const SortIcon = {
    functional: true,
    props: { active: Boolean, direction: String },
    render(createElement, context) {
        const icon = context.props.active
            ? context.props.direction === "asc" ? "arrow_upward" : "arrow_downward"
            : "unfold_more"
        return createElement("i", { class: ["material-icons", "sort-icon", { active: context.props.active }], attrs: { "aria-hidden": "true" } }, icon)
    },
}

export default {
    name: "DrawerActivityTable",
    components: { SortIcon },
    props: {
        safeDrawerId: { type: String, required: true },
    },
    data() {
        return {
            rows: [],
            totalResults: 0,
            totalPages: 0,
            page: 1,
            limit: 25,
            loading: false,
            error: "",
            expandedNoteId: null,
            searchValue: "",
            searchTimeout: null,
            requestSequence: 0,
            sortBy: "occurred_at",
            sortOrder: "desc",
            filters: {
                transactionType: "",
                paymentMethodId: "",
                userKey: "",
            },
            filterOptions: {
                transactionTypes: [],
                paymentMethods: [],
                users: [],
            },
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canViewInvoices() {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId: PERMISSION_CODES.CMS_VIEW_INVOICE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        selectedEmployee() {
            return this.filterOptions.users.find((employee) => this.employeeOptionKey(employee) === this.filters.userKey) || null
        },
        activeFilterCount() {
            return [this.filters.transactionType, this.filters.paymentMethodId, this.filters.userKey].filter(Boolean).length
        },
        hasActiveFilters() {
            return Boolean(this.searchValue.trim() || this.activeFilterCount)
        },
        activeChips() {
            const chips = []
            if (this.filters.transactionType) {
                const type = this.filterOptions.transactionTypes.find((item) => item.code === this.filters.transactionType)
                chips.push({ key: "transactionType", label: `Type: ${type?.name || this.filters.transactionType}` })
            }
            if (this.filters.paymentMethodId) {
                const method = this.filterOptions.paymentMethods.find((item) => String(item.id) === String(this.filters.paymentMethodId))
                chips.push({ key: "paymentMethodId", label: `Payment: ${method?.name || this.filters.paymentMethodId}` })
            }
            if (this.filters.userKey) {
                chips.push({ key: "userKey", label: `User: ${this.selectedEmployee?.name || this.filters.userKey}` })
            }
            return chips
        },
        // Cash-out notes appear once the activity API returns a notes field;
        // older API builds omit it, so the column stays hidden rather than empty.
        showNotes() {
            return this.rows.some((row) => Object.prototype.hasOwnProperty.call(row, "notes"))
        },
        columnCount() {
            return this.showNotes ? 8 : 7
        },
        rangeStart() {
            return this.totalResults ? (this.page - 1) * this.limit + 1 : 0
        },
        rangeEnd() {
            return Math.min(this.page * this.limit, this.totalResults)
        },
    },
    watch: {
        safeDrawerId: {
            immediate: true,
            handler(value) {
                if (value) this.fetchActivity()
            },
        },
    },
    mounted() {
        document.addEventListener("click", this.onDocumentClick)
    },
    beforeDestroy() {
        document.removeEventListener("click", this.onDocumentClick)
        clearTimeout(this.searchTimeout)
    },
    methods: {
        employeeOptionKey(employee) {
            return employee.id ? `id:${employee.id}` : `name:${employee.name}`
        },
        requestParams() {
            const params = {
                page: this.page,
                limit: this.limit,
                search: this.searchValue.trim() || undefined,
                transaction_types: this.filters.transactionType || undefined,
                payment_method_ids: this.filters.paymentMethodId || undefined,
                sort_by: this.sortBy,
                sort_order: this.sortOrder,
            }
            if (this.selectedEmployee?.id) {
                params.user_id = this.selectedEmployee.id
                params.user_name = this.selectedEmployee.name
            } else if (this.selectedEmployee?.name) {
                params.user_name = this.selectedEmployee.name
            }
            return params
        },
        async fetchActivity() {
            const requestId = ++this.requestSequence
            this.loading = true
            this.error = ""
            try {
                const data = await CashManagementFetchApi.fetchSafeOrDrawerActivity(this.safeDrawerId, this.requestParams())
                if (requestId !== this.requestSequence) return
                this.rows = Array.isArray(data.result) ? data.result : []
                this.expandedNoteId = null
                this.totalResults = Number(data.totalResults || 0)
                this.totalPages = Number(data.totalPages || 0)
                this.page = Number(data.currentPage || this.page)
                this.limit = Number(data.limit || this.limit)
                this.filterOptions = {
                    transactionTypes: data.filters?.transactionTypes || [],
                    paymentMethods: data.filters?.paymentMethods || [],
                    users: data.filters?.users || [],
                }
            } catch (error) {
                if (requestId !== this.requestSequence) return
                this.rows = []
                this.totalResults = 0
                this.totalPages = 0
                this.error = error.response?.data?.message || "Could not load drawer activity."
            } finally {
                if (requestId === this.requestSequence) this.loading = false
            }
        },
        queueSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.page = 1
                this.fetchActivity()
            }, 300)
        },
        applySelection() {
            this.page = 1
            this.closeFilters()
            this.fetchActivity()
        },
        removeFilter(key) {
            this.filters[key] = ""
            this.page = 1
            this.fetchActivity()
        },
        clearFilters() {
            clearTimeout(this.searchTimeout)
            this.searchValue = ""
            this.filters = { transactionType: "", paymentMethodId: "", userKey: "" }
            this.page = 1
            this.closeFilters()
            this.fetchActivity()
        },
        closeFilters() {
            this.$refs.filtersMenu?.removeAttribute("open")
        },
        onDocumentClick(event) {
            if (this.$refs.toolbar && !this.$refs.toolbar.contains(event.target)) this.closeFilters()
        },
        changeSort(sortBy) {
            if (this.sortBy === sortBy) this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc"
            else {
                this.sortBy = sortBy
                this.sortOrder = sortBy === "occurred_at" ? "desc" : "asc"
            }
            this.page = 1
            this.fetchActivity()
        },
        changePage(page) {
            if (page < 1 || page > this.totalPages || page === this.page) return
            this.page = page
            this.fetchActivity()
        },
        noteText(row) {
            return String(row.notes || "").trim()
        },
        toggleNote(id) {
            this.expandedNoteId = this.expandedNoteId === id ? null : id
        },
        canOpenDetail(row) {
            return row.sourceKind !== "invoice" || this.canViewInvoices
        },
        isRefund(row) {
            return row.transactionTypeCode === "REFUND"
        },
        typeTone(code) {
            if (code === "SALE") return "sale"
            if (code === "REFUND") return "refund"
            return "operational"
        },
        formatDate(value) {
            if (!value) return "—"
            return new Intl.DateTimeFormat("en-CA", {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(value))
        },
        formatCurrency(value, row) {
            const amount = Math.abs(Number(value || 0))
            const formatted = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(amount)
            return this.isRefund(row) && amount ? `-${formatted}` : formatted
        },
        paymentTitle(paymentMethods) {
            return paymentMethods.map((payment) => `${payment.name}: ${this.formatPaymentAmount(payment.amount)}`).join("\n")
        },
        formatPaymentAmount(value) {
            return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(value || 0))
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.drawer-activity { margin-top: 24px; }
.activity-heading, .activity-heading > div, .activity-toolbar, .activity-footer, .pagination-controls, .payment-summary { display: flex; align-items: center; }
.activity-heading { justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.activity-heading > div { gap: 9px; }
.activity-heading h2 { color: $primaryColor; font-size: 1.25rem; font-weight: 750; margin: 0; }
.activity-count { align-items: center; background: rgba($primaryColor, .08); border-radius: 999px; color: rgba($primaryColor, .78); display: inline-flex; font-size: .75rem; font-weight: 700; min-height: 24px; padding: 3px 9px; }
.clear-all-button { background: transparent; border: 0; color: $accentColor; cursor: pointer; font-size: .8rem; font-weight: 700; padding: 5px; }
.activity-toolbar { flex-wrap: wrap; gap: 8px; margin-bottom: 10px; position: relative; }
.search-control { align-items: center; border: 1px solid $lightBorderColor; border-radius: $component-border-radius; display: flex; flex: 1 1 280px; max-width: 430px; min-height: $component-height; padding: 0 10px; }
.search-control i { color: #6b7280; font-size: 19px; }
.search-control input { background: transparent; border: 0; color: $textColor; flex: 1; font-size: $component-font-size; min-width: 0; outline: none; padding: 0 8px; }
.filters-menu { position: relative; }
.filters-trigger { align-items: center; border: 1px solid rgba($primaryColor, .35); border-radius: $component-border-radius; color: $primaryColor; cursor: pointer; display: flex; font-size: $component-font-size; font-weight: 700; gap: 7px; list-style: none; min-height: $component-height; padding: 0 11px; }
.filters-trigger::-webkit-details-marker { display: none; }
.filters-trigger > i { font-size: 18px; }
.filter-count { background: $secondaryColor; border-radius: 999px; color: white; font-size: .7rem; min-width: 20px; padding: 2px 6px; text-align: center; }
.filter-chevron { transition: transform .15s ease; }
.filters-menu[open] .filter-chevron { transform: rotate(180deg); }
.filters-popover { background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: 8px; box-shadow: 0 12px 28px rgba(13,45,44,.16); display: grid; gap: 12px; left: 0; min-width: 300px; padding: 14px; position: absolute; top: calc(100% + 6px); z-index: 20; }
.popover-heading { display: flex; flex-direction: column; }
.popover-heading strong { color: $primaryColor; font-size: .95rem; }
.popover-heading span { color: #6b7280; font-size: .75rem; }
.filter-field { display: grid; gap: 5px; }
.filter-field > span { color: #4b5563; font-size: .72rem; font-weight: 750; letter-spacing: .02em; text-transform: uppercase; }
.filter-field select { background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: $component-border-radius; color: $textColor; font-size: $component-font-size; min-height: $component-height; padding: 0 10px; width: 100%; }
.active-filter-chips { display: flex; flex: 1 1 250px; flex-wrap: wrap; gap: 6px; min-width: 0; }
.filter-chip { align-items: center; background: rgba($secondaryColor, .12); border: 1px solid rgba($secondaryColor, .38); border-radius: 999px; color: darken($secondaryColor, 18%); cursor: pointer; display: inline-flex; font-size: .72rem; font-weight: 700; gap: 4px; max-width: 220px; min-height: 28px; padding: 3px 8px 3px 10px; }
.filter-chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filter-chip i { font-size: 15px; }
.activity-table-wrap { border: 1px solid $lightBorderColor; border-radius: 8px; overflow-x: auto; }
.activity-table { border-collapse: collapse; min-width: 660px; width: 100%; }
.activity-table th { background: rgba($primaryColor, .035); color: #4b5563; font-size: $tableHeaderFontSize; font-weight: 750; letter-spacing: .025em; padding: 9px 7px; text-align: left; text-transform: uppercase; white-space: nowrap; }
.activity-table td { border-top: 1px solid $lightBorderColor; color: $textColor; font-size: .82rem; padding: 9px 7px; vertical-align: middle; }
.activity-table tbody tr:hover { background: rgba($primaryColor, .025); }
.sort-button { align-items: center; background: transparent; border: 0; color: inherit; cursor: pointer; display: inline-flex; font: inherit; gap: 4px; padding: 0; text-transform: inherit; }
::v-deep .sort-icon { color: #9ca3af; font-size: 15px; }
::v-deep .sort-icon.active { color: $secondaryColor; }
.amount-column { font-variant-numeric: tabular-nums; font-weight: 700; text-align: right !important; white-space: nowrap; }
.amount-sort { justify-content: flex-end; width: 100%; }
.amount-column.refund { color: $accentColor; }
.date-cell { min-width: 108px; }
.reference-cell { font-weight: 650; max-width: 145px; overflow-wrap: anywhere; }
.reference-cell a { color: #225ca8; }
.type-badge { border-radius: 999px; display: inline-block; font-size: .7rem; font-weight: 750; padding: 3px 8px; white-space: nowrap; }
.type-badge.sale { background: rgba($successColor, .12); color: darken($successColor, 24%); }
.type-badge.refund { background: rgba($accentColor, .12); color: $accentColor; }
.type-badge.operational { background: rgba($secondaryColor, .15); color: darken($secondaryColor, 20%); }
.payment-summary { gap: 5px; line-height: 1.25; }
.user-column { max-width: 110px; overflow-wrap: anywhere; }
.payment-more { background: rgba($primaryColor, .08); border-radius: 999px; color: $primaryColor; font-size: .68rem; font-weight: 750; padding: 2px 6px; }
.empty-value { color: #9ca3af; }
.notes-column { max-width: 220px; }
.notes-cell { background: transparent; border: 0; color: inherit; cursor: pointer; display: block; font: inherit; max-width: 220px; overflow: hidden; padding: 0; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.notes-cell.expanded { overflow-wrap: anywhere; white-space: pre-wrap; }
.activity-state { color: #6b7280; padding: 28px !important; text-align: center !important; }
.error-state { background: rgba($accentColor, .06); border: 1px solid rgba($accentColor, .22); border-radius: 8px; display: flex; justify-content: space-between; }
.error-state button { background: transparent; border: 0; color: $accentColor; cursor: pointer; font-weight: 750; }
.activity-footer { color: #6b7280; font-size: .77rem; justify-content: space-between; min-height: 46px; padding: 8px 2px 0; }
.pagination-controls { gap: 10px; }
.pagination-controls button { align-items: center; background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: $component-border-radius; color: $primaryColor; cursor: pointer; display: inline-flex; font-size: .75rem; font-weight: 700; gap: 3px; min-height: 32px; padding: 0 8px; }
.pagination-controls button:disabled { cursor: default; opacity: .42; }
.pagination-controls i { font-size: 17px; }
.sr-only { clip: rect(0,0,0,0); clip-path: inset(50%); height: 1px; overflow: hidden; position: absolute; white-space: nowrap; width: 1px; }

@media (max-width: 767px) {
    .drawer-activity { margin-top: 18px; }
    .activity-heading { align-items: flex-start; }
    .search-control { flex-basis: calc(100% - 104px); }
    .active-filter-chips { flex-basis: 100%; }
    .filters-popover { left: auto; max-width: calc(100vw - 36px); min-width: 280px; right: 0; }
    .activity-footer { align-items: flex-start; flex-direction: column; gap: 8px; }
}

@media (max-width: 1399px) {
    .device-column { display: none; }
}
</style>
